import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import ContentContainer from "templates/content-container/contentContainer";
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import { PAGE_IDS } from "app/data/common/pageIds";
// REVERT: legacy Table — see routeGuideColumns.tsx + DataTable
// import Table from "ui-components/table/table";
import { DataTable } from "ui-components/data-table";
import Button, { ButtonThemes, ButtonSizes } from "ui-components/button/button";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import { ReactComponent as TerminalActivityIcon } from "../../images/TerminalAtivityLog.svg";
import { usaStates } from "app/data/common/usaStates";
import {
  DIRECT_FILTER_OPTIONS,
  ZipRoutingProfile,
  formatTerminalRichDisplay,
} from "app/data/zip-routing-profile/models";
import { useZipRoutingProfileList } from "hooks/useZipRoutingProfiles";
import { useTerminalsList } from "hooks/useEquipment";
import { useDataTableColumnPreferences } from "hooks/useDataTableColumnPreferences";
import RouteGuideAddEdit from "./routeGuideAddEdit";
import RouteGuideActivityLog from "./activity-log/routeGuideActivityLog";
import { getRouteGuideColumns } from "./routeGuideColumns";
import {
  ROUTE_GUIDE_COLUMN_OPTIONS,
  ROUTE_GUIDE_COLUMN_PREFS_INSTANCE_KEY,
  ROUTE_GUIDE_COLUMNS_HIDDEN_BY_DEFAULT,
} from "./routeGuideColumnOptions";
import "./routeGuide.scss";

interface Option {
  label: string;
  value: string;
}

const RouteGuide: React.FC = () => {
  const { data: terminalOptions = [], isLoading: terminalsLoading } = useTerminalsList();

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [stateOption, setStateOption] = useState<Option | null>(null);
  const [terminalOption, setTerminalOption] = useState<Option | null>(null);
  const [directOption, setDirectOption] = useState<Option | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const tableDisabled = showCreate || !!editingProfileId || showActivityLog;

  const columnPrefs = useDataTableColumnPreferences({
    instanceKey: ROUTE_GUIDE_COLUMN_PREFS_INSTANCE_KEY,
    columnOptions: ROUTE_GUIDE_COLUMN_OPTIONS,
    hiddenByDefault: ROUTE_GUIDE_COLUMNS_HIDDEN_BY_DEFAULT,
  });

  const terminalFilterOptions: Option[] = terminalOptions.map((t) => ({
    label: formatTerminalRichDisplay(t),
    value: String(t.terminalNumber),
  }));

  const terminalsByNumber = useMemo(() => {
    const map = new Map<string, { airportCode?: string; terminalName?: string }>();
    terminalOptions.forEach((t) => {
      if (t.terminalNumber == null) return;
      map.set(String(t.terminalNumber), {
        airportCode: t.airportCode,
        terminalName: t.terminalName,
      });
    });
    return map;
  }, [terminalOptions]);

  const columns = useMemo(
    () => getRouteGuideColumns(terminalsByNumber),
    [terminalsByNumber]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useZipRoutingProfileList({
    search: appliedSearch || undefined,
    state: stateOption?.value,
    terminalNumber: terminalOption?.value,
    direct: directOption?.value,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // REVERT: legacy Table infinite scroll (DOM query on .xgs-route-guide__orders)
  // const handleScroll = useCallback(() => {
  //   const scrollContainer = document.querySelector(".xgs-route-guide__orders");
  //   if (!scrollContainer) return;
  //   const bottom =
  //     scrollContainer.scrollHeight - scrollContainer.scrollTop <=
  //     scrollContainer.clientHeight + 10;
  //   if (bottom && hasNextPage && !isFetchingNextPage) {
  //     fetchNextPage();
  //   }
  // }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const onSearchStart = useCallback(() => {
    setAppliedSearch(searchInput.trim());
  }, [searchInput]);

  const onSearchClear = () => {
    setSearchInput("");
    setAppliedSearch("");
    setStateOption(null);
    setTerminalOption(null);
    setDirectOption(null);
  };

  return (
    <ContentContainer
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.ROUTE_GUIDE}
          title="Route Guide"
          status="PREVIEW"
          tooltip="Routing Guide Maintenance (replaces AS400 RTE021). Large catalog — filters use server-side pagination."
          className="xgs-route-guide-toolbar"
        >
          <div className="xgs-route-guide-toolbar__activity-log">
            <Button
              theme={ButtonThemes.transparent}
              size={ButtonSizes.auto}
              onClick={() => setShowActivityLog(true)}
              disabled={tableDisabled && !showActivityLog}
            >
              <div className="xgs-btn__icon-container">
                <TerminalActivityIcon className="xgs-btn__icon" />
              </div>
              Route Guide Activity Log
            </Button>
          </div>
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-route-guide">
        <div className="xgs-list__controls">
          <Formik
            initialValues={{
              state: stateOption?.value || "",
              terminal: terminalOption?.value || "",
              direct: directOption?.value || "",
            }}
            enableReinitialize
            onSubmit={onSearchStart}
          >
            {() => (
              <div className="xgs-list__controls__search">
                <LabeledTextInput
                  label="Search ZIP"
                  labelMode={LabelModes.column}
                  className="xgs-list__controls__search__input"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !isLoading && setAppliedSearch(searchInput.trim())
                  }
                  type="text"
                  disabled={tableDisabled}
                  placeholder="Search by ZIP code..."
                />

                <LabeledSelectInput
                  label="State"
                  labelMode={LabelModes.column}
                  isClearable
                  isSearchable
                  onValueChange={(opt) => setStateOption(opt as Option | null)}
                  value={stateOption}
                  options={usaStates as Option[]}
                  className="xgs-item-form__field filter-select"
                  disabled={tableDisabled}
                  placeholder="All States"
                />

                <LabeledSelectInput
                  label="Terminal"
                  labelMode={LabelModes.column}
                  isClearable
                  isSearchable
                  isLoading={terminalsLoading}
                  onValueChange={(opt) => setTerminalOption(opt as Option | null)}
                  value={terminalOption}
                  options={terminalFilterOptions}
                  className="xgs-item-form__field filter-select"
                  disabled={tableDisabled}
                  placeholder="All Terminals"
                />

                <LabeledSelectInput
                  label="Delivery Point"
                  labelMode={LabelModes.column}
                  isClearable
                  isSearchable={false}
                  onValueChange={(opt) => setDirectOption(opt as Option | null)}
                  value={directOption}
                  options={DIRECT_FILTER_OPTIONS}
                  className="xgs-item-form__field filter-select"
                  disabled={tableDisabled}
                  placeholder="All Delivery Points"
                />

                <div className="xgs-route-guide__filter-actions">
                  <Button
                    className="xgs-list__controls__button"
                    theme={ButtonThemes.blue}
                    disabled={isLoading || tableDisabled}
                    onClick={onSearchStart}
                  >
                    Search
                  </Button>

                  <Button
                    className="xgs-list__controls__button"
                    theme={ButtonThemes.gray}
                    disabled={tableDisabled}
                    onClick={onSearchClear}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            )}
          </Formik>

          <div className="xgs-list__controls__actions">
            <Button
              className="xgs-route-guide__create-button"
              theme={ButtonThemes.blue}
              disabled={tableDisabled}
              onClick={() => setShowCreate(true)}
            >
              + Add Profile
            </Button>
          </div>
        </div>

        {error && <XGSErrorMessage>{(error as Error)?.message || String(error)}</XGSErrorMessage>}

        {!error && (
          <div className="xgs-route-guide__orders" style={{ position: "relative" }}>
            {/* REVERT: legacy Table
            <Table
              isLoading={isLoading}
              columns={columns}
              data={data?.allItems || []}
              noResultsText="There are no route guide profiles available"
              infiniteScroll
              infiniteScrollLoading={isFetchingNextPage}
              infiniteScrollHasNext={!!hasNextPage}
              onInfiniteScroll={handleScroll}
              responsive={true}
              sorting={true}
              minTableHeight={420}
              cursorPointer={!tableDisabled}
              onRowClicked={(row: ZipRoutingProfile) => {
                if (!tableDisabled && row?.id) {
                  setEditingProfileId(row.id);
                }
              }}
              autoResetSelectedRows={true}
            />
            */}
            <DataTable
              data={data?.allItems || []}
              columns={columns}
              isLoading={isLoading}
              isBlocked={tableDisabled}
              cursorPointer={!tableDisabled}
              onRowClicked={(row: ZipRoutingProfile) => {
                if (!tableDisabled && row?.id) setEditingProfileId(row.id);
              }}
              noResultsText="There are no route guide profiles available"
              responsive
              sorting
              minTableHeight={420}
              stickyHeaders
              infiniteScroll
              infiniteScrollHasNext={!!hasNextPage}
              infiniteScrollLoading={isFetchingNextPage}
              onInfiniteScroll={() => {
                if (hasNextPage && !isFetchingNextPage) fetchNextPage();
              }}
              enableColumnResize
              enableColumnReorder
              showColumnSelector
              columnSelectorOptions={ROUTE_GUIDE_COLUMN_OPTIONS}
              {...columnPrefs}
            />
          </div>
        )}

        <RouteGuideAddEdit
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setAppliedSearch("");
            refetch();
          }}
        />

        <RouteGuideAddEdit
          show={!!editingProfileId}
          profileId={editingProfileId}
          onClose={() => setEditingProfileId(null)}
          onSaved={() => refetch()}
        />

        <RouteGuideActivityLog
          show={showActivityLog}
          onClose={() => setShowActivityLog(false)}
        />
      </div>
    </ContentContainer>
  );
};

export default RouteGuide;
